import type { Schema } from '@lemonade-hq/chat-engine';
import type { OptionallyConditionalItem } from '@lemonade-hq/chat-wizard-common-definitions';
import type { GenericSchemaDataValue } from '@lemonade-hq/maschema-schema';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type { IconName, ToastOpenProps } from '@lemonade-hq/uniclient-theme';
import type { UniclientAction, UniclientBaseAction, UniclientChangeValue } from './action';
import { cart } from './cart';
import { checkout, checkoutPlan } from './checkout';
import type { UniclientComponent } from './component';
import type { UniclientImage } from './components/image';
import { hero, page, pageSection } from './page';
import { paymentMethod } from './payment';
import { summary } from './summary';
import type { FormattedText } from './utils';

/* TYPES */

export interface UniclientDialogStep<TComponentAttributes extends string = string> {
  /** @optional the thumbnail to be displayed in the dialog */
  readonly thumbnail?: UniclientImage | string;
  /** Dialog title */
  readonly title: string;
  /** @optional Dialog subtitle */
  readonly subtitle?: FormattedText;
  /** @optional Dialog body content can be any uniclient component including content if you need content*/
  readonly component?: UniclientComponent<TComponentAttributes> | UniclientComponent<TComponentAttributes>[];
  /** Primary dialog step action. It can be a single action or an array of actions. */
  readonly action: UniclientAction<TComponentAttributes>;
  /** @optional Secondary dialog step action. It can be a single action or an array of actions. */
  readonly secondaryAction?: UniclientAction<TComponentAttributes>;
  /** @optional The layout of the actions, default is `row` */
  readonly actionsLayout?: 'column' | 'row';
  /** @optional The bottom action to be displayed in the dialog */
  readonly bottomAction?: UniclientAction<TComponentAttributes>;
  /** @optional Left align component content   */
  readonly alignComponentLeft?: boolean;
}

export type UniclientDialogData<TComponentAttributes extends string = string, TDialogSchema extends Schema = Schema> = {
  /** @optional The initial values for the dialog. It will be used to populate the schema. */
  readonly initialValues?: Record<TComponentAttributes, GenericSchemaDataValue>;
  /** A schema to be attached to the dialog component. */
  readonly schema: TDialogSchema;
};

export type UniclientDialogProps<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
  TData extends UniclientDialogData<TComponentAttributes, TDialogSchema> = UniclientDialogData<
    TComponentAttributes,
    TDialogSchema
  >,
> = {
  /** for analytics purposes all dialogs must have a unique name */
  readonly name: string;
  /** @optional in mobile, opens the drawer to capture the entire screen */
  readonly fullscreen?: boolean;
  /** @optional Whether the dialog is dismissible @default true */
  readonly dismissible?: boolean;
  /** The content of the dialog. It can be a single step or an array of steps. */
  readonly content: UniclientDialogStep<TComponentAttributes> | UniclientDialogStep<TComponentAttributes>[];
  /** @optional The data to be attached to the dialog. */
  readonly data?: TData;
  /** @optional Added analytics properties to dialog events. */
  readonly analytics?: Record<string, string>;
  /** @optional dialog size */
  readonly size?: 'large' | 'small';
  /** @optional Whether the dialog should be dynamic height based on the content */
  readonly dynamicHeight?: boolean;
  /** @optional The top action to be displayed in the dialog */
  readonly topAction?: UniclientAction & { readonly icon: IconName };
  /** @optional The disclaimer to be displayed under the actions */
  readonly disclaimer?: string;
  /** @optional Whether the dialog is loading */
  readonly loading?: boolean;
};

export type UniclientOpenDialogAction<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
> = UniclientBaseAction<'openDialog'> & {
  readonly dialog: UniclientDialogProps<TComponentAttributes, TDialogSchema>;
};

export type UniclientOpenDynamicDialogWithDataOverrides<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
> = {
  readonly name: string;
  readonly data?: Partial<UniclientDialogData<TComponentAttributes, TDialogSchema>> & {
    readonly resyncActions?: Record<string, UniclientAction<TComponentAttributes>>;
  };
};

export type UniclientOpenDynamicDialogAction<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
> = UniclientBaseAction<'openDialog'> & {
  readonly dialog: UniclientOpenDynamicDialogWithDataOverrides<TComponentAttributes, TDialogSchema> | string;
};

export type UniclientCloseDialogAction = UniclientBaseAction<'closeDialog'>;

export type UniclientOpenToastAction = Omit<ToastOpenProps, 'type'> &
  UniclientBaseAction<'openToast'> & {
    readonly toastType: ToastOpenProps['type'];
  };

interface UniclientDialogChangeValue<
  TTargetAttributes extends string = string,
  TDialogAttributes extends string = string,
> {
  /** The original schema attribute to be changed. */
  readonly targetAttribute: TTargetAttributes;
  /** The dialog schema attribute to take value from, will use the entire dialog answers as the value if left empty */
  readonly dialogAttribute?: TDialogAttributes;
  /**
   * @optional Strategy to change the value, default is `replace`
   * Look at `UniclientChangeValue['strategy']` for more details
   */
  readonly strategy?: UniclientChangeValue['strategy'];
}

export type UniclientSaveDialogValuesAction<TComponentAttributes extends string = string> =
  UniclientBaseAction<'saveDialogValues'> & {
    readonly data: UniclientDialogChangeValue<TComponentAttributes>[];
  };

export type UniclientDynamicDialogProps<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
> = UniclientDialogProps<
  TComponentAttributes,
  TDialogSchema,
  UniclientDialogData<TComponentAttributes, TDialogSchema> & {
    /** @optional Will perform the action with the dialog attribute (key) is changed, will only resync action in the dialog if the conditions are met */
    readonly resyncActions?: Record<string, OptionallyConditionalItem<UniclientAction<TComponentAttributes>>>;
    readonly resyncingDebounce?: Milliseconds;
  }
>;

export type UniclientDynamicDialog<
  TComponentAttributes extends string = string,
  TDialogSchema extends Schema = Schema,
> = Omit<UniclientDynamicDialogProps<TComponentAttributes, TDialogSchema>, 'name'>;

export function dynamicDialog<TComponentAttributes extends string = string, TDialogSchema extends Schema = Schema>(
  props: UniclientDynamicDialog<TComponentAttributes, TDialogSchema>,
): UniclientDynamicDialog<TComponentAttributes, TDialogSchema> {
  return props;
}

/* ACTIONS */

export function openDialog(
  props: Omit<UniclientOpenDialogAction | UniclientOpenDynamicDialogAction, 'type'> | string,
): UniclientOpenDialogAction | UniclientOpenDynamicDialogAction {
  return typeof props === 'string' ? { dialog: props, type: 'openDialog' } : { ...props, type: 'openDialog' };
}

export function closeDialog(props?: Omit<UniclientCloseDialogAction, 'type'>): UniclientCloseDialogAction {
  return { ...props, type: 'closeDialog' };
}

export function saveDialogValues(
  props: Omit<UniclientSaveDialogValuesAction, 'type'>,
): UniclientSaveDialogValuesAction {
  return { ...props, type: 'saveDialogValues' };
}

export function openToast(props: Omit<UniclientOpenToastAction, 'type'>): UniclientOpenToastAction {
  return { ...props, type: 'openToast' };
}

export const LAYOUTS = {
  summary,
  dialog: Object.assign(dynamicDialog, {
    action: { open: openDialog, close: closeDialog, saveValues: saveDialogValues },
  }),
  toast: {
    open: openToast,
  },
};

export const PAGES = Object.assign(page, {
  section: Object.assign(pageSection, { hero }),
  checkout: Object.assign(checkout, { method: paymentMethod, plan: checkoutPlan }),
  cart,
});
